import { useEffect } from "react";
import { track } from "../../lib/events";
import { activeMilliseconds, activeSecondsSince } from "../../lib/activeTime";
import { Status } from "../ui";
import { Quiz } from "../quiz/Quiz";
import type { QuizControl } from "../quiz/Quiz";
import { Form } from "../form/Form";
import { CONTENT_RENDERERS } from "./index";
import type { ContentItem } from "../../types";

export interface ContentViewProps {
  item: ContentItem;
  moduleID: string;
  onComplete: () => void;
  moduleTitle?: string;
  onQuizControl?: (control: QuizControl | null) => void;
}

// ContentView dispatches to the renderer registry — one component per
// content type — plus the two response-collecting types (quiz/form) that
// keep their own submission lifecycle. A type not yet in the registry
// falls back to a clear "not supported" message rather than a silent
// blank screen.
export function ContentView({
  item,
  moduleID,
  onComplete,
  moduleTitle,
  onQuizControl,
}: ContentViewProps) {
  useEffect(() => {
    const startedAt = Date.now();
    const activeAtStart = activeMilliseconds();
    track("item_opened", {
      module_id: moduleID,
      item_id: item.id,
      payload: { item_type: item.type },
    });
    return () =>
      track("page_closed", {
        module_id: moduleID,
        item_id: item.id,
        payload: {
          duration: Math.round((Date.now() - startedAt) / 1000),
          active_seconds: activeSecondsSince(activeAtStart),
          page: "item",
        },
      });
  }, [item.id, item.type, moduleID]);

  if (item.type === "quiz") {
    return (
      <Quiz
        item={item}
        moduleTitle={moduleTitle}
        onComplete={onComplete}
        onControl={onQuizControl}
      />
    );
  }
  if (item.type === "form") return <Form item={item} onComplete={onComplete} />;

  const Renderer = CONTENT_RENDERERS[item.type];
  if (!Renderer) {
    return (
      <Status
        variant="error"
        title="Unsupported content"
        message={`Content type "${item.type}" cannot be displayed yet.`}
      />
    );
  }
  return <Renderer item={item} moduleID={moduleID} onComplete={onComplete} />;
}
